/* A customer with no app, no code and no account.
 *
 * ENROLMENT IS NOT AUTHENTICATION. Every plan to cut SMS quietly deleted the
 * only enrolment path there was, because a new customer at a till has no
 * account for a stamp to attach to. So the cashier types the number, the stamp
 * lands against it unverified, and the customer proves the number later at
 * their own pace. "Scan this to start" is a two-minute app install with a
 * queue behind it.
 *
 * THE CONSENT LINE IS THE POINT OF THIS SCREEN. Under the PDPPL a spoken
 * sentence is NOTICE; consent has to be recorded, with how and when. So the
 * exact string shown here is the string the cashier says aloud, the string on
 * the printed counter card, and the string written into the ledger's consent
 * record -- one ENROL_LINE, below, so two records cannot disagree. The tick
 * is what turns a notice into a consent, and the button will not send without
 * it.
 *
 * AN EMAIL, OPTIONAL, AND NOT FOR SENDING ANYTHING (047). SMS sign-in is
 * parked, so a customer enrolled here cannot sign in with this number, and a
 * Google sign-in makes an account nothing connects to the card. An address
 * given here lets the card find that account at sign-in. It has its own line
 * and its own tick, because agreeing to be stamped is not agreeing to have an
 * address kept, and leaving it empty changes nothing about the stamp.
 *
 * THE READ-BACK IS THE CONTROL. A mistyped address bounces nothing -- nothing
 * is ever sent -- it silently gives the card to whoever owns the typo. So when
 * an address is given, nothing is sent until the cashier has read it back and
 * the customer has said it is right, on a screen that shows it split at the @
 * and large enough to read letter by letter.
 *
 * WHAT IS DELIBERATELY NOT ON THIS SCREEN: any promise to text them. No SMS
 * sender ID is registered with Ooredoo or Vodafone, nothing has been filed,
 * and approval runs weeks after a clean submission. And no claim about where
 * the number is held: true today because nothing sends, false the moment a
 * provider is switched on, and this screen outlives that change.
 */
import { Button } from "@damga/ds";
import { t } from "@damga/kit";
import { useEffect, useRef, useState } from "react";

export type EnrolInput = {
  phone: string;
  name: string | null;
  consentText: string;
  /** Only when one was given, agreed to, and read back. */
  email?: { address: string; consentText: string } | null;
};

/* Where a long address may wrap: after a dot, a dash or an underscore, which is
 * where a person reading it aloud pauses anyway. The first render broke
 * "Noora.AlMansouri" as "Noora.AlMansour" and a lone "i" on the next line --
 * on the one screen whose job is that nobody misreads a letter. */
function breakable(part: string) {
  const pieces = part.split(/(?<=[._-])/);
  return pieces.map((piece, i) => (
    <span key={i}>
      {piece}
      {i < pieces.length - 1 && <wbr />}
    </span>
  ));
}

/* The same shape the server checks: a mailbox, an @, a domain with a dot. Not a
 * validator -- it cannot catch a typo -- which is what the read-back is for. */
const LOOKS_LIKE_EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

interface Props {
  /** Sends it. Rejects like any other call; the caller renders the outcome. */
  enrol(input: EnrolInput): Promise<void>;
  onBack(): void;
  busy: boolean;
  /** Prefilled when the cashier came here from a lookup that found nobody. */
  initialPhone?: string;
}

/* STAGING SAYS STAGING'S SENTENCE (2026-09-18). The server keeps a consent only
 * under the exact wording on file, and staging's approved wording is synthetic
 * and signed with a key only staging holds (DECISIONS, 2026-09-17). This screen
 * said the copy table's sentence everywhere, so staging's till could enrol
 * nobody: every attempt was E_CONSENT_WORDING_MISMATCH, and staging's customers
 * existed only because the seeder wrote them. A staging build passes the
 * synthetic sentences in (infra/deploy-staging-hosting.sh); production's never
 * does, infra/deploy-web.sh refuses a bundle carrying one, and a production till
 * built with one anyway would still fail closed -- production refuses synthetic
 * wording it cannot verify. One constant per line, used for what is shown and
 * what is recorded alike. */
const ENROL_LINE: string = import.meta.env.VITE_STAGING_CONSENT_ENROLMENT || t("enrol.consent");
const EMAIL_LINE: string = import.meta.env.VITE_STAGING_CONSENT_CARD_EMAIL || t("enrolEmail.consent");

export function Enrol({ enrol, onBack, busy, initialPhone }: Props) {
  const [phone, setPhone] = useState(initialPhone ?? "");
  const [name, setName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [nagged, setNagged] = useState(false);
  const [email, setEmail] = useState("");
  const [emailAgreed, setEmailAgreed] = useState(false);
  const [emailSay, setEmailSay] = useState<string | null>(null);
  // Set once the form is complete and an address was given: the next screen is
  // the read-back, and nothing has been sent.
  const [readingBack, setReadingBack] = useState(false);
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Focus the number unless it arrived filled in, in which case the cashier
    // has already typed it once and the next thing they need is the tick.
    if (!initialPhone) field.current?.focus();
  }, [initialPhone]);

  const ready = phone.replace(/\D/g, "").length >= 8;
  const address = email.trim();

  const send = (withEmail: boolean) =>
    void enrol({
      phone,
      name: name.trim() || null,
      consentText: ENROL_LINE,
      email: withEmail ? { address, consentText: EMAIL_LINE } : null,
    });

  if (readingBack) {
    const at = address.lastIndexOf("@");
    return (
      <section className="lookup">
        <p className="label">{t("enrolEmail.readBack")}</p>
        {/* Split at the @ and set large: the two halves are read differently
            aloud ("noora dot almansouri", "at gmail dot com"), and a letter
            that is wrong is found by looking, not by hoping. */}
        <p className="readback" aria-live="polite">
          <span className="readback-local">{breakable(address.slice(0, at))}</span>
          <span className="readback-domain">{breakable(address.slice(at))}</span>
        </p>
        <p className="muted lookup-hint">{t("enrolEmail.readBackHint")}</p>
        <Button
          size="counter"
          variant="primary"
          disabled={busy}
          onClick={() => send(true)}
          fullWidth
        >
          {busy ? t("common.loading") : t("enrolEmail.right")}
        </Button>
        <Button size="counter" variant="ghost" onClick={() => setReadingBack(false)} fullWidth>
          {t("enrolEmail.change")}
        </Button>
      </section>
    );
  }

  return (
    <section className="lookup">
      <p className="label">{t("enrol.title")}</p>

      {/* noValidate: an `type="email"` field otherwise hands a partial address
          to the browser's own validation bubble, which is in the browser's
          words, easy to miss on a tablet, and stops the form before our own
          sentence can be shown. Found by the proof, 2026-09-18. */}
      <form
        className="enrol-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (!agreed) {
            setNagged(true);
            return;
          }
          if (!ready || busy) return;
          if (address) {
            // An address given has to be a whole one and agreed to separately.
            // Either failing stops here with a sentence, rather than quietly
            // stamping without it: the customer was just told their card
            // would be in the app.
            if (!LOOKS_LIKE_EMAIL.test(address)) {
              setEmailSay(t("enrolEmail.notValid"));
              return;
            }
            if (!emailAgreed) {
              setEmailSay(t("enrolEmail.needConsent"));
              return;
            }
            setEmailSay(null);
            setReadingBack(true);
            return;
          }
          // The sentence as rendered, not a key. What is stored has to be what
          // was shown, in the language it was shown in.
          send(false);
        }}
      >
        <input
          ref={field}
          className="till-input"
          type="tel"
          inputMode="tel"
          autoComplete="off"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder={t("enrol.placeholder")}
          aria-label={t("enrol.placeholder")}
          disabled={busy}
        />
        <input
          className="till-input"
          type="text"
          autoComplete="off"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("enrol.nameLabel")}
          aria-label={t("enrol.nameLabel")}
          disabled={busy}
        />
        <p className="muted lookup-hint">{t("enrol.nameHint")}</p>

        <input
          className="till-input"
          type="email"
          inputMode="email"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setEmailSay(null);
          }}
          placeholder={t("enrolEmail.label")}
          aria-label={t("enrolEmail.label")}
          disabled={busy}
        />
        <p className="muted lookup-hint">{t("enrolEmail.hint")}</p>

        {/* UNTICKED, AND THE FORM WILL NOT SEND WITHOUT IT. A pre-ticked box is
            not freely given consent, and this is the one control on the screen
            that a regulator would ask about. */}
        <label className="enrol-consent">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => {
              setAgreed(e.target.checked);
              if (e.target.checked) setNagged(false);
            }}
            disabled={busy}
          />
          <span>
            <strong>{t("enrol.agreed")}</strong>
            <span className="enrol-said">{ENROL_LINE}</span>
          </span>
        </label>

        {nagged && !agreed && <p className="till-error">{t("enrol.needConsent")}</p>}

        {/* ITS OWN LINE AND ITS OWN TICK, shown only when an address is being
            given. Unticked, like the one above, for the same reason. */}
        {address && (
          <label className="enrol-consent">
            <input
              type="checkbox"
              checked={emailAgreed}
              onChange={(e) => {
                setEmailAgreed(e.target.checked);
                if (e.target.checked) setEmailSay(null);
              }}
              disabled={busy}
            />
            <span>
              <strong>{t("enrolEmail.agreed")}</strong>
              <span className="enrol-said">{EMAIL_LINE}</span>
            </span>
          </label>
        )}
        {emailSay && <p className="till-error">{emailSay}</p>}

        <Button size="counter" variant="primary" type="submit" disabled={busy || !ready} fullWidth>
          {busy ? t("common.loading") : t("enrol.submit")}
        </Button>
      </form>

      <Button size="counter" variant="ghost" onClick={onBack} fullWidth>
        {t("lookup.back")}
      </Button>
    </section>
  );
}
